import { useUIColors } from '@/core/hooks';
import React, { useMemo } from 'react';
import { ActivityIndicator, Dimensions, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Typography } from './Typography';

export interface NumberPadButtonProps {
    number: string | number;
    onPress: (number: string | number) => void;
    disabled?: boolean;
    variant?: 'number' | 'delete' | 'empty' | 'submit';
    size?: number; // height per key row
    margin?: number; // margin around button
    loading?: boolean;
}

export const NumberPadButton = React.memo<NumberPadButtonProps>(function NumberPadButton({
    number,
    onPress,
    disabled = false,
    variant = 'number',
    size = 64,
    margin,
    loading = false,
}) {
    const colors = useUIColors();
    
    // Calculate responsive text size based on screen height (only if gaps are already reduced)
    const screenHeight = Dimensions.get('window').height;
    // Match thresholds with NumberPad component
    const isVerySmallScreen = screenHeight < 650;
    const isSmallScreen = screenHeight < 750;
    
    // Only reduce text size if screen is very small (after gaps are already reduced)
    const textSize = useMemo(() => {
        if (isVerySmallScreen) return 18; // Aggressive reduction for very small screens
        if (isSmallScreen) return 20; // Moderate reduction for small screens
        return 24; // Normal size for large screens
    }, [isSmallScreen, isVerySmallScreen]);
    
    const buttonMargin = margin ?? (isVerySmallScreen ? 3 : isSmallScreen ? 4 : 8);

    const handlePress = () => {
        if (!disabled && !loading && variant !== 'empty') {
            onPress(number);
        }
    };

    const getButtonStyle = () => {
        if (variant === 'submit') {
            return {
                height: size,
                minWidth: 115, // matches figma cell width
                backgroundColor: 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
            } as const;
        }
        return {
            height: size,
            minWidth: 56,
            backgroundColor: 'transparent',
        } as const;
    };

    const getTextColor = () => {
        if (disabled) return 'fontTertiary';
        if (variant === 'empty') return 'transparent';
        return 'fontPrimary';
    };

    return (
        <TouchableOpacity
            style={[styles.container, getButtonStyle(), { margin: buttonMargin }]}
            onPress={handlePress}
            disabled={disabled || loading || variant === 'empty'}
            activeOpacity={0.7}
        >
            <View style={styles.content}>
                {variant === 'number' && (
                    <Typography variant="headline" color={getTextColor()} style={[styles.numberText, { fontSize: textSize, lineHeight: textSize + 4 }]}>
                        {number}
                    </Typography>
                )}
                {variant === 'delete' && (
                    <Typography variant="headline" color={getTextColor()} style={[styles.deleteText, { fontSize: textSize, lineHeight: textSize + 4 }]}>
                        ⌫
                    </Typography>
                )}
                {variant === 'submit' && (
                    <View style={{ backgroundColor: colors.buttonDefault, borderRadius: 8, padding: 16, minWidth: 48, minHeight: 48, justifyContent: 'center', alignItems: 'center' }}>
                        {loading ? (
                            <ActivityIndicator size="small" color="white" />
                        ) : (
                            <Typography variant="headline" color="white" style={{ fontSize: 20 }}>➔</Typography>
                        )}
                    </View>
                )}
                {variant === 'empty' && <View style={{ width: 24, height: 24 }} />}
            </View>
        </TouchableOpacity>
    );
});

const styles = StyleSheet.create({
    container: {
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 56, // Ensure minimum height to prevent clipping
    },
    content: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    numberText: {
        fontSize: 24, // Increased from 20 to prevent clipping on iPhone
        fontWeight: '500',
        lineHeight: 28, // Add line height to prevent clipping
    },
    deleteText: {
        fontWeight: '500',
        // Font size is set dynamically via textSize prop to match number text size
    },
});

export default NumberPadButton;
